import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import CalendarIcon from '../../assets/icons/calendar.svg?react';
import './Calendar.css';

const Calendar = ({ 
  mode = 'single', // 'single' | 'range'
  monthShortCut = true,
  value = null,
  onChange = () => {},
  placeholder = '날짜를 선택하세요',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('day'); // 'month' | 'day'
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [tempSelection, setTempSelection] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);

  // 날짜 포맷팅
  const formatDate = (date) => {
    if (!date) return placeholder;
    if (mode === 'range' && Array.isArray(date) && date.length === 2) {
      return `${dayjs(date[0]).format('YYYY.MM.DD')} - ${dayjs(date[1]).format('YYYY.MM.DD')}`;
    }
    return dayjs(date).format('YYYY.MM.DD');
  };

  // 달력 데이터 생성
  const generateCalendarData = (month) => {
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfWeek = startOfMonth.startOf('week');
    const endOfWeek = endOfMonth.endOf('week');
    
    const days = [];
    let current = startOfWeek;
    
    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }
    
    return days;
  };

  // 날짜 선택 처리
  const handleDateClick = useCallback((date) => {
    if (disabled) return;

    if (mode === 'single') {
      setTempSelection(date);
      onChange(date);
      setIsOpen(false);
    } else if (mode === 'range') {
      if (!tempSelection) {
        // 첫 번째 날짜 선택
        setTempSelection(date);
        // 범위 모드에서는 첫 번째 선택 후 달력을 열어둠
      } else {
        // 두 번째 날짜 선택 또는 같은 날짜 재선택
        if (tempSelection.isSame(date, 'day')) {
          // 같은 날짜를 다시 클릭하면 초기화
          setTempSelection(null);
        } else {
          // 두 번째 날짜 선택
          const start = tempSelection.isBefore(date) ? tempSelection : date;
          const end = tempSelection.isAfter(date) ? tempSelection : date;
          setTempSelection(null);
          onChange([start, end]);
          setIsOpen(false);
        }
      }
    }
  }, [mode, tempSelection, onChange, disabled, value]);

  // 오늘 날짜로 돌아가기
  const handleGoToToday = () => {
    setCurrentMonth(dayjs());
  };

  // 날짜 상태 확인
  const getDateState = (date) => {
    const isCurrentMonth = date.isSame(currentMonth, 'month');
    const isToday = date.isSame(dayjs(), 'day');
    
    let isSelected = false;
    let isInRange = false;
    let isTempSelected = false;
    
    if (mode === 'single') {
      isSelected = tempSelection && date.isSame(tempSelection, 'day');
    } else if (mode === 'range') {
      // 임시 선택 중인 경우 우선 표시 (새로운 범위 선택 시작)
      if (tempSelection) {
        isTempSelected = date.isSame(tempSelection, 'day');
        // 호버된 날짜와 임시 선택 날짜 사이의 범위 표시
        if (hoveredDate && tempSelection) {
          const start = tempSelection.isBefore(hoveredDate) ? tempSelection : hoveredDate;
          const end = tempSelection.isAfter(hoveredDate) ? tempSelection : hoveredDate;
          if (date.isAfter(start, 'day') && date.isBefore(end, 'day')) {
            isInRange = true;
          }
        }
      } else {
        // 완료된 범위 선택 (tempSelection이 없을 때만)
        if (Array.isArray(value) && value.length === 2) {
          isSelected = value.some(d => d && date.isSame(d, 'day'));
          isInRange = date.isAfter(value[0], 'day') && date.isBefore(value[1], 'day');
        }
      }
    }
    
    const isHovered = hoveredDate && date.isSame(hoveredDate, 'day');

    return {
      isCurrentMonth,
      isToday,
      isSelected,
      isInRange,
      isTempSelected,
      isHovered
    };
  };

  // 월 선택 처리
  const handleMonthClick = useCallback((monthIndex) => {
    if (disabled) return;
    
    // 현재 년도와 선택한 월로 날짜 생성
    const selectedMonth = dayjs().year(currentMonth.year()).month(monthIndex);
    const startOfMonth = selectedMonth.startOf('month');
    const endOfMonth = selectedMonth.endOf('month');
    
    // 해당 월의 처음과 끝을 범위로 설정
    onChange([startOfMonth, endOfMonth]);
    setTempSelection(null);
    setIsOpen(false);
  }, [currentMonth, onChange, disabled]);

  // 월 선택 뷰 렌더링
  const renderMonthSelector = () => {
    const months = Array.from({ length: 12 }, (_, i) => i);
    
    // 현재 선택된 범위가 있는 경우 해당 월 확인
    const getMonthSelectionState = (monthIndex) => {
      if (Array.isArray(value) && value.length === 2) {
        const startMonth = dayjs(value[0]).month();
        const startYear = dayjs(value[0]).year();
        const endMonth = dayjs(value[1]).month();
        const endYear = dayjs(value[1]).year();
        const currentYear = currentMonth.year();
        
        // 같은 년도의 같은 월이 선택된 경우
        return startMonth === monthIndex && 
               endMonth === monthIndex && 
               startYear === currentYear && 
               endYear === currentYear;
      }
      return false;
    };

    return (
      <div className="calendar-month-selector">
        <div className="calendar-header">
          <button 
            className="calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.subtract(1, 'year'))}
          >
            <ChevronLeftIcon/>
          </button>
          <span className="calendar-month-year">
            {currentMonth.format('YYYY년')}
          </span>
          <button 
            className="calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.add(1, 'year'))}
          >
            <ChevronLeftIcon style={{ transform: 'rotate(180deg)' }}/>
          </button>
        </div>
        
        <div className="calendar-months-grid">
          {months.map((monthIndex) => {
            const isSelected = getMonthSelectionState(monthIndex);
            return (
              <button
                key={monthIndex}
                className={`calendar-month-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleMonthClick(monthIndex)}
                disabled={disabled}
              >
                {monthIndex + 1}월
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // 일 선택 뷰 렌더링
  const renderDaySelector = () => {
    const days = generateCalendarData(currentMonth);
    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
      <div className="calendar-day-selector">
        <div className="calendar-header">
          <button 
            className="calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.subtract(1, 'month'))}
          >
            <ChevronLeftIcon/>
          </button>
          <span className="calendar-month-year">
            {currentMonth.format('YYYY년 M월')}
          </span>
          <button 
            className="calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.add(1, 'month'))}
          >
            <ChevronLeftIcon style={{ transform: 'rotate(180deg)' }}/>
          </button>
        </div>
        
        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days.map((date, index) => {
            const state = getDateState(date);
            return (
              <button
                key={index}
                className={`calendar-day ${!state.isCurrentMonth ? 'other-month' : ''} ${
                  state.isSelected ? 'selected' : ''
                } ${state.isTempSelected ? 'temp-selected' : ''} ${
                  state.isInRange ? 'in-range' : ''
                } ${state.isHovered ? 'hovered' : ''} ${
                  state.isToday ? 'today' : ''
                }`}
                onClick={() => handleDateClick(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
                disabled={disabled}
              >
                {date.date()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // 달력 렌더링
  const renderCalendar = () => {
    return (
      <div className="calendar-container">
        {/* 탭 헤더 */}
        {monthShortCut && (
        <div className="calendar-tabs">
          <button
            className={`calendar-tab ${activeTab === 'month' ? 'active' : ''}`}
            onClick={() => setActiveTab('month')}
            disabled={disabled}
          >
            월 선택
          </button>
          <button
            className={`calendar-tab ${activeTab === 'day' ? 'active' : ''}`}
            onClick={() => setActiveTab('day')}
            disabled={disabled}
          >
            일 선택
          </button>
        </div>
        )}
        
        {/* 탭 내용 */}
        {activeTab === 'month' ? renderMonthSelector() : renderDaySelector()}
      </div>
    );
  };

  // 달력 열기/닫기 처리
  const handleToggleCalendar = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className={`calendar-wrapper ${className}`}>
      <div 
        className={`calendar-input ${disabled ? 'disabled' : ''}`}
        onClick={handleToggleCalendar}
      >
        <span className="calendar-text">{formatDate(value)}</span>
        <span className="calendar-icon"><CalendarIcon color='var(--gray-700)'/></span>
      </div>
      
      {isOpen && (
        <div className="calendar-dropdown">
          {renderCalendar()}
          <div className="calendar-actions">
            <button 
              className="calendar-btn today"
              onClick={handleGoToToday}
            >
              오늘
            </button>
            <div className="calendar-actions-right">
              <button 
                className="calendar-btn cancel"
                onClick={() => {
                  setTempSelection(null);
                  // 취소 시 값 변경 없이 달력만 닫기
                  setIsOpen(false);
                }}
              >
                취소
              </button>
              <button 
                className="calendar-btn confirm"
                onClick={() => {
                  // 범위 모드에서 임시 선택이 있으면 완료 처리
                  if (mode === 'range' && tempSelection) {
                    // 임시 선택만 있고 두 번째 선택이 없는 경우, 같은 날짜로 범위 설정
                    onChange([tempSelection, tempSelection]);
                  }
                  setTempSelection(null);
                  setIsOpen(false);
                }}
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
